// frontend/src/components/ChatBubble.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, IconButton, Typography, Avatar, TextField, Paper,
  Badge, Tooltip, Divider, CircularProgress, Zoom
} from '@mui/material';
import {
  Chat as ChatIcon,
  Close as CloseIcon,
  Send as SendIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';
import chatAPI from '../api/chatAPI';

// This runs continuously for as long as anyone is logged in, whether or not
// they ever open chat — a 5s ambient poll meant one browser tab alone
// produced 12 requests/minute doing nothing but asking "anything new?".
// 20s keeps the unread badge feeling current without that cost.
const AMBIENT_POLL_MS = 20_000;
// Only runs while a conversation is actually open on screen — a shorter
// interval here is reasonable since it's bounded to genuinely active use,
// not a permanent background cost.
const ACTIVE_POLL_MS = 8_000;

const initials = (name) =>
  (name || '?').split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase();

const avatarColor = (name) => {
  const colors = ['#3b82f6','#0d9488','#7c3aed','#d97706','#dc2626','#059669','#db2777'];
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

export default function ChatBubble({ user }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('users'); // 'users' | 'chat'
  const [teamUsers, setTeamUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [sending, setSending] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch team users once
  useEffect(() => {
    if (!user) return;
    setLoadingUsers(true);
    chatAPI.getTeamUsers()
      .then(u => setTeamUsers((u || []).filter(u2 => u2.id !== user.id && u2.is_active !== false)))
      .catch(() => {})
      .finally(() => setLoadingUsers(false));
  }, [user]);

  // Poll unread count
  useEffect(() => {
    if (!user) return;
    const poll = () => {
      chatAPI.getUnreadCount().then(c => setUnreadTotal(c || 0)).catch(() => {});
    };
    poll();
    const t = setInterval(poll, AMBIENT_POLL_MS);
    return () => clearInterval(t);
  }, [user]);

  // Poll messages when chat is open
  const fetchMessages = useCallback(() => {
    if (!selectedUser) return;
    chatAPI.getMessages(selectedUser.id)
      .then(msgs => setMessages(msgs || []))
      .catch(() => {});
  }, [selectedUser]);

  useEffect(() => {
    if (!open || view !== 'chat' || !selectedUser) return;
    setLoadingMessages(true);
    chatAPI.getMessages(selectedUser.id)
      .then(msgs => {
        setMessages(msgs || []);
        // Mark as read
        chatAPI.markRead(selectedUser.id).catch(() => {});
      })
      .catch(() => {})
      .finally(() => setLoadingMessages(false));

    pollRef.current = setInterval(() => {
      fetchMessages();
      chatAPI.markRead(selectedUser.id).catch(() => {});
    }, ACTIVE_POLL_MS);

    return () => clearInterval(pollRef.current);
  }, [open, view, selectedUser, fetchMessages]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSelectUser = (u) => {
    setSelectedUser(u);
    setView('chat');
    setTimeout(() => inputRef.current?.focus(), 200);
  };

  const handleBack = () => {
    setView('users');
    setSelectedUser(null);
    setMessages([]);
    clearInterval(pollRef.current);
  };

  const handleSend = async () => {
    const text = newMessage.trim();
    if (!text || !selectedUser) return;
    setSending(true);
    setNewMessage('');
    try {
      await chatAPI.sendMessage(selectedUser.id, text);
      fetchMessages();
    } catch (e) {
      setNewMessage(text); // restore on error
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      {/* Chat Panel */}
      <Zoom in={open} unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: { xs: 80, sm: 90 },
            right: { xs: 12, sm: 24 },
            width: { xs: 'calc(100vw - 24px)', sm: 340 },
            maxWidth: 380,
            height: { xs: 460, sm: 500 },
            borderRadius: 3,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1300,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)'
          }}
        >
          {/* Header */}
          <Box sx={{
            background: 'linear-gradient(90deg, #1e3a8a 0%, #1d4ed8 60%, #0d9488 100%)',
            color: 'white',
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexShrink: 0
          }}>
            {view === 'chat' ? (
              <IconButton size="small" onClick={handleBack} sx={{ color: 'white', mr: 0.5, p: 0.5 }}>
                <BackIcon fontSize="small" />
              </IconButton>
            ) : null}
            {view === 'chat' && selectedUser ? (
              <>
                <Avatar sx={{ width: 30, height: 30, fontSize: '0.75rem', bgcolor: avatarColor(selectedUser.name) }}>
                  {initials(selectedUser.name)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', lineHeight: 1.1 }} noWrap>
                    {selectedUser.name}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', opacity: 0.8 }} noWrap>
                    {selectedUser.role}
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                <ChatIcon fontSize="small" />
                <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', flex: 1 }}>
                  Team Chat
                </Typography>
              </>
            )}
            <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'white', p: 0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Body */}
          {view === 'users' ? (
            // User list
            <Box sx={{ flex: 1, overflowY: 'auto', bgcolor: 'background.paper' }}>
              <Typography sx={{ px: 2, py: 1, fontSize: '0.75rem', color: 'text.secondary', fontWeight: 600, letterSpacing: 0.5 }}>
                TEAM MEMBERS
              </Typography>
              <Divider />
              {loadingUsers ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : teamUsers.length === 0 ? (
                <Typography sx={{ p: 2, color: 'text.secondary', fontSize: '0.85rem', textAlign: 'center' }}>
                  No team members found
                </Typography>
              ) : (
                teamUsers.map(u => (
                  <Box
                    key={u.id}
                    onClick={() => handleSelectUser(u)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      px: 2, py: 1.25,
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                      '&:hover': { bgcolor: 'action.hover' },
                      borderBottom: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Badge
                      overlap="circular"
                      badgeContent={u.unread_count > 0 ? u.unread_count : 0}
                      color="error"
                      sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 16, height: 16 } }}
                    >
                      <Avatar sx={{ width: 36, height: 36, fontSize: '0.8rem', bgcolor: avatarColor(u.name) }}>
                        {initials(u.name)}
                      </Avatar>
                    </Badge>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 500, fontSize: '0.875rem', lineHeight: 1.2 }} noWrap>
                        {u.name}
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', textTransform: 'capitalize' }} noWrap>
                        {u.role}
                      </Typography>
                    </Box>
                    {u.last_message && (
                      <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled' }} noWrap>
                        {formatTime(u.last_message_at)}
                      </Typography>
                    )}
                  </Box>
                ))
              )}
            </Box>
          ) : (
            // Messages view
            <>
              <Box sx={{
                flex: 1, overflowY: 'auto',
                px: 2, py: 1.5,
                display: 'flex', flexDirection: 'column', gap: 1,
                bgcolor: '#f8fafc'
              }}>
                {loadingMessages ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : messages.length === 0 ? (
                  <Box sx={{ textAlign: 'center', pt: 3 }}>
                    <Typography sx={{ fontSize: '2rem' }}>👋</Typography>
                    <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mt: 1 }}>
                      Start a conversation with {selectedUser?.name}
                    </Typography>
                  </Box>
                ) : (
                  messages.map((msg, idx) => {
                    const isMe = msg.sender_id === user?.id;
                    return (
                      <Box
                        key={msg.id || idx}
                        sx={{
                          display: 'flex',
                          justifyContent: isMe ? 'flex-end' : 'flex-start',
                          gap: 0.75,
                          alignItems: 'flex-end'
                        }}
                      >
                        {!isMe && (
                          <Avatar sx={{
                            width: 24, height: 24, fontSize: '0.6rem',
                            bgcolor: avatarColor(msg.sender_name || selectedUser?.name),
                            flexShrink: 0
                          }}>
                            {initials(msg.sender_name || selectedUser?.name)}
                          </Avatar>
                        )}
                        <Box sx={{ maxWidth: '72%' }}>
                          <Box sx={{
                            px: 1.5, py: 1,
                            borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                            background: isMe
                              ? 'linear-gradient(135deg, #1d4ed8 0%, #0d9488 100%)'
                              : 'white',
                            color: isMe ? 'white' : 'text.primary',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                            wordBreak: 'break-word'
                          }}>
                            <Typography sx={{ fontSize: '0.84rem', lineHeight: 1.45 }}>
                              {msg.message}
                            </Typography>
                          </Box>
                          <Typography sx={{
                            fontSize: '0.65rem',
                            color: 'text.disabled',
                            mt: 0.25,
                            textAlign: isMe ? 'right' : 'left',
                            px: 0.5
                          }}>
                            {formatTime(msg.created_at)}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </Box>

              {/* Input */}
              <Box sx={{
                px: 1.5, py: 1.25,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                display: 'flex', gap: 1, alignItems: 'flex-end',
                flexShrink: 0
              }}>
                <TextField
                  inputRef={inputRef}
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a message..."
                  multiline
                  maxRows={3}
                  size="small"
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: 3, fontSize: '0.875rem' }
                  }}
                />
                <IconButton
                  onClick={handleSend}
                  disabled={!newMessage.trim() || sending}
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, #1d4ed8, #0d9488)',
                    color: 'white',
                    width: 36, height: 36,
                    flexShrink: 0,
                    '&:hover': { background: 'linear-gradient(135deg, #1e40af, #0f766e)' },
                    '&.Mui-disabled': { background: '#e0e0e0', color: '#bdbdbd' }
                  }}
                >
                  {sending ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <SendIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </Box>
            </>
          )}
        </Paper>
      </Zoom>

      {/* Floating Chat Button */}
      <Tooltip title="Team Chat" placement="left">
        <Box
          sx={{
            position: 'fixed',
            bottom: { xs: 80, sm: 88 },
            right: { xs: 16, sm: 24 },
            zIndex: 1200
          }}
        >
          <Badge
            badgeContent={unreadTotal}
            color="error"
            sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem', minWidth: 18, height: 18 } }}
          >
            <IconButton
              onClick={() => setOpen(prev => !prev)}
              sx={{
                width: 52, height: 52,
                background: open
                  ? 'linear-gradient(135deg, #1e293b, #334155)'
                  : 'linear-gradient(135deg, #1d4ed8 0%, #0d9488 100%)',
                color: 'white',
                boxShadow: '0 4px 20px rgba(29,78,216,0.4)',
                transition: 'all 0.25s ease',
                '&:hover': {
                  transform: 'scale(1.08)',
                  boxShadow: '0 6px 24px rgba(29,78,216,0.5)'
                }
              }}
            >
              {open ? <CloseIcon /> : <ChatIcon />}
            </IconButton>
          </Badge>
        </Box>
      </Tooltip>
    </>
  );
}
